import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { SystemStyleObject } from '@chakra-ui/styled-system';
import type { InvoicePart } from '../../types/invoice';
import type { LineItemField, LineItemRowSnapshot } from './InvoiceLineItemsSection';

export const LINE_ITEM_PASTE_COL_FIELDS: readonly LineItemField[] = [
    'title',
    'partNumber',
    'qty',
    'listPrice',
    'unitBuyPrice',
    'totalBuyPrice',
];

export const LINE_ITEM_PASTE_COL_COUNT = LINE_ITEM_PASTE_COL_FIELDS.length;

const MAX_PASTE_UNDO_STACK = 25;

type CellPos = { row: number; col: number };
type PasteUndoBlock = LineItemRowSnapshot[];

function normalizeRect(a: CellPos | null, b: CellPos | null): { r0: number; r1: number; c0: number; c1: number } | null {
    if (!a || !b) return null;
    return {
        r0: Math.min(a.row, b.row),
        r1: Math.max(a.row, b.row),
        c0: Math.min(a.col, b.col),
        c1: Math.max(a.col, b.col),
    };
}

function parsePastedValue(field: LineItemField, raw: string): string | number {
    const t = raw.replace(/\u00a0/g, ' ').trim();
    if (field === 'title' || field === 'partNumber') return t;
    if (field === 'qty') {
        const n = parseInt(t.replace(/,/g, ''), 10);
        return Number.isFinite(n) ? n : 0;
    }
    const cleaned = t.replace(/[$,\s]/g, '');
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
}

function escapeTsvCell(s: string): string {
    if (s.includes('\n') || s.includes('\r') || s.includes('\t')) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

function parseTsvRows(text: string): string[][] {
    const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
    return lines.map((line) => {
        const row: string[] = [];
        let cur = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (inQuotes) {
                if (ch === '"') {
                    if (line[i + 1] === '"') {
                        cur += '"';
                        i++;
                    } else {
                        inQuotes = false;
                    }
                } else {
                    cur += ch;
                }
            } else if (ch === '\t') {
                row.push(cur);
                cur = '';
            } else if (ch === '"') {
                inQuotes = true;
            } else {
                cur += ch;
            }
        }
        row.push(cur);
        return row;
    });
}

function blurInputsInside(container: HTMLElement | null) {
    if (!container) return;
    const ae = document.activeElement;
    if (ae instanceof HTMLElement && container.contains(ae) && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA')) {
        ae.blur();
    }
}

export interface UseInvoicePartGridSpreadsheetSelectionArgs {
    rowCount: number;
    rows: ReadonlyArray<InvoicePart>;
    getStringValue: (index: number, field: LineItemField, defaultValue?: string) => string;
    updateCell: (index: number, field: LineItemField, value: unknown) => void;
    /** Defaults to the same 6 columns as line items / freight / misc / ignored grids. */
    pasteColFields?: readonly LineItemField[];
    /** When set, ⌘Z / Ctrl+Z after paste restores these row snapshots. */
    restoreRowsSnapshot?: (snapshots: ReadonlyArray<LineItemRowSnapshot>) => void;
    isReadOnly: boolean;
}

export function useInvoicePartGridSpreadsheetSelection({
    rowCount,
    rows,
    getStringValue,
    updateCell,
    pasteColFields = LINE_ITEM_PASTE_COL_FIELDS,
    restoreRowsSnapshot,
    isReadOnly,
}: UseInvoicePartGridSpreadsheetSelectionArgs) {
    const pasteColCount = pasteColFields.length;
    const containerRef = useRef<HTMLDivElement>(null);
    const pasteUndoStackRef = useRef<PasteUndoBlock[]>([]);
    const [anchor, setAnchor] = useState<CellPos | null>(null);
    const [focus, setFocus] = useState<CellPos | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [selectionCopiedHighlight, setSelectionCopiedHighlight] = useState(false);
    const focusContainerAfterSelectRef = useRef(false);

    const fieldForCol = useCallback((col: number) => pasteColFields[col], [pasteColFields]);

    const clearSelection = useCallback(() => {
        setAnchor(null);
        setFocus(null);
    }, []);

    const selectionRect = normalizeRect(anchor, focus);

    const selectionBoundsKey = selectionRect
        ? `${selectionRect.r0},${selectionRect.r1},${selectionRect.c0},${selectionRect.c1}`
        : '';

    useEffect(() => {
        setSelectionCopiedHighlight(false);
    }, [selectionBoundsKey]);

    const isCellInSelection = useCallback(
        (row: number, col: number) => {
            const r = selectionRect;
            if (!r) return false;
            return row >= r.r0 && row <= r.r1 && col >= r.c0 && col <= r.c1;
        },
        [selectionRect]
    );

    const getCellPasteSelectionSx = useCallback(
        (row: number, col: number): SystemStyleObject | undefined => {
            const r = selectionRect;
            if (!r || row < r.r0 || row > r.r1 || col < r.c0 || col > r.c1) return undefined;
            const outer = selectionCopiedHighlight
                ? 'var(--chakra-colors-yellow-500)'
                : 'var(--chakra-colors-blue-600)';
            const inner = selectionCopiedHighlight ? 'rgba(180, 83, 9, 0.5)' : 'rgba(30, 64, 175, 0.55)';
            return {
                borderTop: row === r.r0 ? `2px solid ${outer}` : undefined,
                borderLeft: col === r.c0 ? `2px solid ${outer}` : undefined,
                borderRight: col === r.c1 ? `2px solid ${outer}` : `1px dotted ${inner}`,
                borderBottom: row === r.r1 ? `2px solid ${outer}` : `1px dotted ${inner}`,
            };
        },
        [selectionRect, selectionCopiedHighlight]
    );

    const buildTsvFromSelection = useCallback(() => {
        const r = selectionRect;
        if (!r) return '';
        const lines: string[] = [];
        for (let row = r.r0; row <= r.r1; row++) {
            const cells: string[] = [];
            for (let col = r.c0; col <= r.c1; col++) {
                const field = fieldForCol(col);
                if (!field) continue;
                const raw = row < rowCount ? getStringValue(row, field, '') : '';
                cells.push(escapeTsvCell(raw));
            }
            lines.push(cells.join('\t'));
        }
        return lines.join('\n');
    }, [fieldForCol, getStringValue, rowCount, selectionRect]);

    const applyPaste = useCallback(
        (text: string) => {
            const r = selectionRect;
            if (!r || isReadOnly) return;
            setSelectionCopiedHighlight(false);
            blurInputsInside(containerRef.current);
            const grid = parseTsvRows(text);
            if (grid.length === 0) return;
            const startRow = r.r0;
            const startCol = r.c0;

            const touchedRows = new Set<number>();
            for (let pr = 0; pr < grid.length; pr++) {
                const row = startRow + pr;
                if (row < 0 || row >= rowCount) continue;
                const line = grid[pr] ?? [];
                for (let pc = 0; pc < line.length; pc++) {
                    const col = startCol + pc;
                    if (col >= 0 && col < pasteColCount) touchedRows.add(row);
                }
            }

            if (restoreRowsSnapshot && touchedRows.size > 0) {
                const snapshots: LineItemRowSnapshot[] = [];
                touchedRows.forEach((row) => {
                    const item = rows[row];
                    if (item) snapshots.push({ row, item: { ...item } });
                });
                snapshots.sort((a, b) => a.row - b.row);
                pasteUndoStackRef.current.push(snapshots);
                if (pasteUndoStackRef.current.length > MAX_PASTE_UNDO_STACK) {
                    pasteUndoStackRef.current.shift();
                }
            }

            for (let pr = 0; pr < grid.length; pr++) {
                const row = startRow + pr;
                if (row < 0 || row >= rowCount) continue;
                const line = grid[pr] ?? [];
                for (let pc = 0; pc < line.length; pc++) {
                    const col = startCol + pc;
                    if (col < 0 || col >= pasteColCount) continue;
                    const field = fieldForCol(col);
                    if (!field) continue;
                    const rawCell = line[pc] ?? '';
                    updateCell(row, field, parsePastedValue(field, rawCell));
                }
            }
        },
        [fieldForCol, isReadOnly, pasteColCount, restoreRowsSnapshot, rows, rowCount, selectionRect, updateCell]
    );

    const resolveCellFromPoint = useCallback(
        (clientX: number, clientY: number): CellPos | null => {
            const el = document.elementFromPoint(clientX, clientY);
            const cell = el?.closest('[data-line-paste-cell]');
            if (!cell || !containerRef.current?.contains(cell)) return null;
            const row = Number(cell.getAttribute('data-row'));
            const col = Number(cell.getAttribute('data-col'));
            if (!Number.isFinite(row) || !Number.isFinite(col)) return null;
            if (row < 0 || row >= rowCount || col < 0 || col >= pasteColCount) return null;
            return { row, col };
        },
        [pasteColCount, rowCount]
    );

    const onContainerMouseDownCapture = useCallback(
        (e: React.MouseEvent) => {
            if (isReadOnly || e.button !== 0) return;
            const target = e.target as HTMLElement;
            if (target.closest('button, [role="button"], a, [role="menuitem"], [role="menuitemcheckbox"]')) return;

            const cellEl = target.closest('[data-line-paste-cell]');
            const inPasteCell = !!cellEl && !!containerRef.current?.contains(cellEl);

            if (!e.metaKey && !e.ctrlKey && !e.shiftKey) {
                if (inPasteCell) clearSelection();
                return;
            }

            if (!inPasteCell || !cellEl) return;

            const row = Number(cellEl.getAttribute('data-row'));
            const col = Number(cellEl.getAttribute('data-col'));
            if (!Number.isFinite(row) || !Number.isFinite(col)) return;
            const pos = { row, col };

            if (e.shiftKey && !e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                if (anchor) setFocus(pos);
                else {
                    setAnchor(pos);
                    setFocus(pos);
                }
                focusContainerAfterSelectRef.current = true;
                return;
            }

            if (e.metaKey || e.ctrlKey) {
                e.preventDefault();
                setAnchor(pos);
                setFocus(pos);
                focusContainerAfterSelectRef.current = true;
                setIsDragging(true);
            }
        },
        [anchor, clearSelection, isReadOnly]
    );

    useEffect(() => {
        if (!isDragging || isReadOnly) return;
        const onMove = (e: MouseEvent) => {
            const pos = resolveCellFromPoint(e.clientX, e.clientY);
            if (pos) setFocus(pos);
        };
        const onUp = () => setIsDragging(false);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [isDragging, isReadOnly, resolveCellFromPoint]);

    useLayoutEffect(() => {
        if (isReadOnly || !anchor || !focus || !focusContainerAfterSelectRef.current) return;
        focusContainerAfterSelectRef.current = false;
        containerRef.current?.focus({ preventScroll: true });
    }, [anchor, focus, isReadOnly]);

    useEffect(() => {
        if (!selectionRect || isReadOnly) return;
        const onKeyDown = (e: KeyboardEvent) => {
            const host = containerRef.current;
            const ae = document.activeElement;
            const focusInside = host && ae instanceof Node && host.contains(ae);
            if (!focusInside) return;

            if (e.key === 'Escape') {
                clearSelection();
                return;
            }
            if (!(e.metaKey || e.ctrlKey)) return;
            if (e.key.toLowerCase() === 'z' && !e.shiftKey) {
                if (!restoreRowsSnapshot || pasteUndoStackRef.current.length === 0) return;
                e.preventDefault();
                e.stopPropagation();
                blurInputsInside(containerRef.current);
                const block = pasteUndoStackRef.current.pop();
                if (block?.length) restoreRowsSnapshot(block);
                return;
            }
            if (e.key === 'c' || e.key === 'C') {
                e.preventDefault();
                e.stopPropagation();
                const tsv = buildTsvFromSelection();
                void navigator.clipboard?.writeText(tsv).then(
                    () => {
                        setSelectionCopiedHighlight(true);
                    },
                    () => {
                        /* denied or unavailable */
                    }
                );
                return;
            }
            if (e.key === 'v' || e.key === 'V') {
                e.preventDefault();
                e.stopPropagation();
                void (async () => {
                    try {
                        const text = await navigator.clipboard.readText();
                        applyPaste(text);
                    } catch {
                        /* ignore */
                    }
                })();
            }
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [applyPaste, buildTsvFromSelection, clearSelection, isReadOnly, restoreRowsSnapshot, selectionRect]);

    const onPaste = useCallback(
        (e: React.ClipboardEvent) => {
            if (!selectionRect || isReadOnly) return;
            const text = e.clipboardData.getData('text/plain');
            if (text === '') return;
            e.preventDefault();
            blurInputsInside(containerRef.current);
            applyPaste(text);
        },
        [applyPaste, isReadOnly, selectionRect]
    );

    return {
        containerRef,
        selectionRect,
        isCellInSelection,
        getCellPasteSelectionSx,
        onContainerMouseDownCapture,
        onPaste,
    };
}
